import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { listProjects } from '../api/projects'
import type { ProjectListItem } from '../types'
import { Layout } from '../components/Layout'
import { Button } from '../components/Button'
import { ProjectCard } from './ProjectCard'
import styles from './ProjectListPage.module.css'

const PROFILE_BANNER_KEY = 'fasico_profile_banner_dismissed'

function SkeletonCards() {
  return (
    <div className={styles.skeleton}>
      {[1,2,3].map(i => <div key={i} className={styles.skeletonCard} />)}
    </div>
  )
}

export function ProjectListPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showProfileBanner, setShowProfileBanner] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listProjects()
      setProjects(data)
    } catch {
      setError('No pudimos cargar tus proyectos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!user?.full_name && !sessionStorage.getItem(PROFILE_BANNER_KEY)) {
      setShowProfileBanner(true)
    }
  }, [user])

  const dismissProfileBanner = () => {
    sessionStorage.setItem(PROFILE_BANNER_KEY, '1')
    setShowProfileBanner(false)
  }

  return (
    <Layout>
      <div className={styles.page}>
        {/* Top bar */}
        <div className={styles.topBar}>
          <div>
            <h1 className={styles.pageTitle}>
              {user?.full_name ? `Hola, ${user.full_name}` : 'Tus proyectos'}
            </h1>
            <p className={styles.pageSubtitle}>Proyectos TE1</p>
          </div>
          <div className={styles.newBtn}>
            <Button onClick={() => navigate('/projects/new')}>
              + Nuevo proyecto
            </Button>
          </div>
        </div>

        <div className={styles.content}>
          {showProfileBanner && (
            <div className={styles.profileBanner} role="note" aria-label="Aviso de perfil incompleto">
              <p className={styles.profileBannerText}>
                Completa tu perfil para personalizar tu experiencia.{' '}
                <Link to="/profile" className={styles.profileBannerLink}>
                  Completar perfil
                </Link>
              </p>
              <button
                className={styles.profileBannerClose}
                onClick={dismissProfileBanner}
                aria-label="Cerrar aviso"
                type="button"
              >
                ✕
              </button>
            </div>
          )}

          {loading && <SkeletonCards />}

          {!loading && error && (
            <div className={styles.errorBox}>
              <p>{error}</p>
              <Button variant="secondary" onClick={load}>Reintentar</Button>
            </div>
          )}

          {!loading && !error && projects.length === 0 && (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden="true">📋</span>
              <h2 className={styles.emptyTitle}>Aún no tienes proyectos</h2>
              <p className={styles.emptySubtitle}>
                Crea tu primer proyecto para generar la documentación TE1
              </p>
              <div className={styles.emptyAction}>
                <Button onClick={() => navigate('/projects/new')} fullWidth>
                  + Nuevo proyecto
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && projects.length > 0 && (
            <div className={styles.list}>
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAB móvil */}
      <button
        className={styles.fab}
        onClick={() => navigate('/projects/new')}
        aria-label="Nuevo proyecto"
      >
        +
      </button>
    </Layout>
  )
}
